import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HiOutlineX } from "react-icons/hi";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import moment from "moment";
import Button from "../../../../components/shared/Button/Button";
import Divider from "../../../../components/shared/Divider/Divider";
import Input from "../../../../components/shared/Input/Input";
import Checkbox from "../../../../components/shared/Checkbox/Checkbox";
import { useAdminSearchStore } from "../../store/adminSearchStore";
import { SubscriptionFilter } from "../UsersView/SubscriptionFilter";

export function SubscriptionsFiltersPanel({ isOpen, onClose, onSubmit }) {
  const { subscriptionsState, setSubscriptionsFilters } = useAdminSearchStore();
  const { filters } = subscriptionsState;

  const formSchema = useMemo(
    () =>
      z.object({
        startDateFrom: z.string().optional(),
        startDateTo: z.string().optional(),
        endDateFrom: z.string().optional(),
        endDateTo: z.string().optional(),
        lastUpdatedFrom: z.string().optional(),
        lastUpdatedTo: z.string().optional(),
      }),
    []
  );

  const [draftFilters, setDraftFilters] = useState({
    userInput: "",
    autoRenewal: null,
    freeTrialUsed: null,
    subscriptionType: [],
  });

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      startDateFrom: "",
      startDateTo: "",
      endDateFrom: "",
      endDateTo: "",
      lastUpdatedFrom: "",
      lastUpdatedTo: "",
    },
    mode: "onChange",
  });

  const watchedDates = watch([
    "startDateFrom",
    "startDateTo",
    "endDateFrom",
    "endDateTo",
    "lastUpdatedFrom",
    "lastUpdatedTo",
  ]);

  useEffect(() => {
    if (isOpen) {
      setDraftFilters({
        userInput: filters.userInput || "",
        autoRenewal:
          typeof filters.autoRenewal === "boolean" ? filters.autoRenewal : null,
        freeTrialUsed:
          typeof filters.freeTrialUsed === "boolean"
            ? filters.freeTrialUsed
            : null,
        subscriptionType: Array.isArray(filters.subscriptionType)
          ? filters.subscriptionType
          : [],
      });
      setValue(
        "startDateFrom",
        filters.startDate?.from
          ? moment(filters.startDate.from).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "startDateTo",
        filters.startDate?.to
          ? moment(filters.startDate.to).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "endDateFrom",
        filters.endDate?.from
          ? moment(filters.endDate.from).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "endDateTo",
        filters.endDate?.to
          ? moment(filters.endDate.to).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "lastUpdatedFrom",
        filters.lastUpdated?.from
          ? moment(filters.lastUpdated.from).format("YYYY-MM-DD")
          : ""
      );
      setValue(
        "lastUpdatedTo",
        filters.lastUpdated?.to
          ? moment(filters.lastUpdated.to).format("YYYY-MM-DD")
          : ""
      );
    }
  }, [filters, isOpen, setValue]);

  const hasActiveFilters = useMemo(() => {
    if ((draftFilters.userInput || "").trim()) return true;
    if (typeof draftFilters.autoRenewal === "boolean") return true;
    if (typeof draftFilters.freeTrialUsed === "boolean") return true;
    if (draftFilters.subscriptionType.length > 0) return true;
    if (watchedDates.some((d) => (d || "").trim())) return true;
    return false;
  }, [draftFilters, watchedDates]);

  const handleClear = useCallback(() => {
    setDraftFilters({
      userInput: "",
      autoRenewal: null,
      freeTrialUsed: null,
      subscriptionType: [],
    });
    reset();
    setSubscriptionsFilters({
      userInput: "",
      autoRenewal: null,
      freeTrialUsed: null,
      startDate: { from: null, to: null },
      endDate: { from: null, to: null },
      lastUpdated: { from: null, to: null },
      subscriptionType: [],
    });
    onSubmit?.();
  }, [setSubscriptionsFilters, onSubmit]);

  const handleFormSubmit = useCallback(
    (data) => {
      setSubscriptionsFilters({
        userInput: draftFilters.userInput || "",
        autoRenewal:
          typeof draftFilters.autoRenewal === "boolean"
            ? draftFilters.autoRenewal
            : null,
        freeTrialUsed:
          typeof draftFilters.freeTrialUsed === "boolean"
            ? draftFilters.freeTrialUsed
            : null,
        startDate: {
          from: data.startDateFrom
            ? moment(data.startDateFrom).startOf("day").valueOf()
            : null,
          to: data.startDateTo
            ? moment(data.startDateTo).endOf("day").valueOf()
            : null,
        },
        endDate: {
          from: data.endDateFrom
            ? moment(data.endDateFrom).startOf("day").valueOf()
            : null,
          to: data.endDateTo
            ? moment(data.endDateTo).endOf("day").valueOf()
            : null,
        },
        lastUpdated: {
          from: data.lastUpdatedFrom
            ? moment(data.lastUpdatedFrom).startOf("day").valueOf()
            : null,
          to: data.lastUpdatedTo
            ? moment(data.lastUpdatedTo).endOf("day").valueOf()
            : null,
        },
        subscriptionType: draftFilters.subscriptionType || [],
      });
      onSubmit?.();
    },
    [draftFilters, onSubmit, setSubscriptionsFilters]
  );

  if (!isOpen) return null;

  return (
    <div
      aria-label="Subscription Filters"
      aria-modal="true"
      className="font-manrope fixed right-0 top-0 z-[2000] flex h-screen w-full max-w-96 flex-col border-l border-gray200 bg-white p-6"
      role="dialog"
    >
      <div className="relative flex flex-col gap-1">
        <div className="text-2xl font-semibold">Filters</div>
        <div className="text-base text-gray600">
          Filter subscriptions by criteria
        </div>
        <button
          aria-label="Close"
          className="absolute right-0 top-0 w-fit rounded-sm p-2"
          onClick={onClose}
        >
          <HiOutlineX className="size-6 text-gray500" />
        </button>
      </div>

      <div className="mt-6 flex flex-1 flex-col gap-6 overflow-y-auto scrollbar-hide">
        <div className="flex flex-col gap-2">
          <label className="text-base text-gray700">Search</label>
          <input
            className="h-12 w-full rounded-lg border border-gray300 px-4 text-base outline-none transition-all duration-300 placeholder:text-gray500 focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
            placeholder="Search by user email, name, or id"
            value={draftFilters.userInput}
            onChange={(e) =>
              setDraftFilters((prev) => ({
                ...prev,
                userInput: e.target.value,
              }))
            }
          />
        </div>

        <div className="flex flex-col gap-2">
          <div className="text-base text-gray700">Subscription Type</div>
          <SubscriptionFilter
            draftSubscriptions={draftFilters.subscriptionType}
            setDraftSubscriptions={(val) =>
              setDraftFilters((prev) => ({ ...prev, subscriptionType: val }))
            }
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <label className="flex items-center gap-2 text-base text-gray700">
            <Checkbox
              isChecked={draftFilters.autoRenewal === true}
              onChange={(checked) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  autoRenewal: checked ? true : null,
                }))
              }
              size="md"
              rounded="md"
            />
            Auto Renewal
          </label>

          <label className="flex items-center gap-2 text-base text-gray700">
            <Checkbox
              isChecked={draftFilters.freeTrialUsed === true}
              onChange={(checked) =>
                setDraftFilters((prev) => ({
                  ...prev,
                  freeTrialUsed: checked ? true : null,
                }))
              }
              size="md"
              rounded="md"
            />
            Free Trial Used
          </label>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Start Date From</label>
            <Input
              control={control}
              name="startDateFrom"
              type="date"
              size="md"
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Start Date To</label>
            <Input
              control={control}
              name="startDateTo"
              type="date"
              size="md"
              fullWidth
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">End Date From</label>
            <Input
              control={control}
              name="endDateFrom"
              type="date"
              size="md"
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">End Date To</label>
            <Input
              control={control}
              name="endDateTo"
              type="date"
              size="md"
              fullWidth
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Last Updated From</label>
            <Input
              control={control}
              name="lastUpdatedFrom"
              type="date"
              size="md"
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-base text-gray700">Last Updated To</label>
            <Input
              control={control}
              name="lastUpdatedTo"
              type="date"
              size="md"
              fullWidth
            />
          </div>
        </div>
      </div>

      <div className="mt-auto">
        <Divider type="horizontal" color="gray200" />

        <div className="flex items-center justify-between pt-4">
          <button
            className={`text-lg font-semibold ${
              !hasActiveFilters ? "text-gray300" : "text-primary700"
            }`}
            onClick={handleClear}
          >
            Clear all
          </button>

          <div className="flex gap-2">
            <Button color="gray100" onClick={onClose} size="md">
              Cancel
            </Button>

            <Button
              onClick={handleSubmit(handleFormSubmit)}
              size="md"
              isDisabled={!isDirty && !hasActiveFilters}
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
